// ── BCHNostr — Video card ─────────────────────────────────────────
//
// The unit of the video feed. Performance is the whole design brief:
//
//   · NO player is ever mounted here. A card is an <img> and some text.
//     The iframe/<video> element only exists on the watch page, so
//     scrolling a hundred cards costs a hundred images, not a hundred
//     third-party players.
//   · Thumbnails are `loading="lazy"` + `decoding="async"`, so the browser
//     fetches them through its own intersection logic.
//   · Each card opts into `content-visibility` via `.feed-post-contain`, so
//     off-screen cards are skipped during layout and paint.
//
// A missing/broken thumbnail degrades to a placeholder — a dead external
// image must never leave a hole in the grid.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Rocket, Megaphone, MessageCircle, Eye, BadgeCheck, Share2 } from 'lucide-react';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import { useVideoVerification, type VerificationLabel } from '../hooks/useCreatorVerification';
import UserBadges from './UserBadges';
import VideoTipControl from './VideoTipControl';
import { BCHLogo } from './Layout';
import {
  videoWatchPath, videoChannelPath, formatVideoDuration, formatPublished,
  formatCount, videoCategoryLabel,
  type BCHVideo, type VideoEngagement, type VideoBoostData,
} from '../lib/video';

interface VideoCardProps {
  video: BCHVideo;
  engagement?: VideoEngagement;
  /** Active promotion, when this video is being promoted. */
  boost?: VideoBoostData;
  /** Layout. 'grid' is the home page; 'row' is compact lists / sidebars. */
  variant?: 'grid' | 'row';
  /**
   * This browser's recorded page views. Shown as "page views", never as
   * "views" — see the analytics honesty note in lib/video.ts.
   */
  localViews?: number;
  /** Recommendation reasons, rendered verbatim so ranking stays inspectable. */
  reasons?: string[];
}

// ── Thumbnail ─────────────────────────────────────────────────────

function Thumbnail({ video, className }: { video: BCHVideo; className: string }) {
  const [failed, setFailed] = useState(false);
  const duration = formatVideoDuration(video.duration);

  return (
    <div className={`relative overflow-hidden rounded-xl bg-muted ${className}`}>
      {video.thumbnailUrl && !failed ? (
        <img
          src={video.thumbnailUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-emerald-500/5">
          <Play className="w-1/5 h-1/5 min-w-4 min-h-4 text-primary/40" />
        </div>
      )}

      {/* Duration badge — bottom right, exactly where a viewer looks. */}
      {duration && (
        <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/80 text-white text-[10px] font-semibold tabular-nums">
          {duration}
        </span>
      )}

      {/* Hover affordance. Purely decorative — never loads a player. */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
        <div className="w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center">
          <Play className="w-4 h-4 text-white ml-0.5" />
        </div>
      </div>
    </div>
  );
}

// ── Promotion label ───────────────────────────────────────────────
//
// Boosted and Sponsored content is labelled unambiguously and is NEVER
// presented as an organic recommendation. The distinction is the payer's
// own declaration: a creator boost says "Boosted", a professional
// promotion says "Sponsored".

export function PromotionBadge({ boost, size = 'md' }: {
  boost: VideoBoostData;
  size?: 'sm' | 'md';
}) {
  const sponsored = boost.sponsored;
  const isSm = size === 'sm';
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider ${
        isSm ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'
      } ${
        sponsored
          ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
          : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
      }`}
      title={sponsored
        ? 'A paid promotion from a professional profile — not an organic recommendation.'
        : 'A paid boost from the creator — not an organic recommendation.'}
    >
      {sponsored
        ? <><Megaphone className={isSm ? 'w-2 h-2' : 'w-2.5 h-2.5'} /> Sponsored</>
        : <><Rocket className={isSm ? 'w-2 h-2' : 'w-2.5 h-2.5'} /> Boosted</>}
    </span>
  );
}

// ── Card ──────────────────────────────────────────────────────────

// Auto-derived creator label: a positive "Verified creator" badge for
// videos from a channel the poster proved they control, or a muted "Shared"
// tag otherwise. Nothing renders while the state is still resolving, and
// nothing renders for non-YouTube videos.
function VideoLabel({ video, compact = false }: { video: BCHVideo; compact?: boolean }) {
  const label: VerificationLabel = useVideoVerification(video);
  if (label === 'unknown') return null;
  if (label === 'verified') {
    return (
      <span
        className={`flex items-center gap-0.5 rounded-full bg-primary/10 text-primary font-semibold ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[9px] px-1.5 py-0.5'}`}
        title="The poster verified they control this YouTube channel"
      >
        <BadgeCheck className="w-2.5 h-2.5" /> Verified creator
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-0.5 rounded-full bg-muted text-muted-foreground/80 font-medium text-[9px] px-1.5 py-0.5"
      title="Shared from YouTube — the poster hasn't verified they're the creator"
    >
      <Share2 className="w-2.5 h-2.5" /> Shared
    </span>
  );
}

export default function VideoCard({
  video, engagement, boost, variant = 'grid', localViews = 0, reasons,
}: VideoCardProps) {
  const { profile } = useNostrProfile(video.pubkey);
  const name = useDisplayName(video.pubkey, profile, video.pubkey.slice(0, 8));
  const watchPath = videoWatchPath(video.pubkey, video.id);
  const published = formatPublished(video.publishedAt);

  // ── Compact row (sidebars, related, search results) ──
  if (variant === 'row') {
    return (
      <div className="flex gap-2.5 group feed-post-contain">
        <Link to={watchPath} className="shrink-0 w-[9.5rem] sm:w-[10.5rem]">
          <Thumbnail video={video} className="aspect-video" />
        </Link>
        <div className="min-w-0 flex-1 py-0.5">
          <Link to={watchPath}>
            <h3 className="text-[13px] font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
              {video.title}
            </h3>
          </Link>
          <Link
            to={videoChannelPath(video.pubkey)}
            className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
          >
            <span className="truncate max-w-[8rem]">{name}</span>
            <UserBadges pubkey={video.pubkey} size="sm" showTooltip={false} />
          </Link>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-muted-foreground">
            {published && <span>{published}</span>}
            {engagement && engagement.uniqueTippers > 0 && (
              <span className="flex items-center gap-0.5 text-primary font-medium">
                <BCHLogo size={9} /> {engagement.uniqueTippers}
              </span>
            )}
            {boost && <PromotionBadge boost={boost} size="sm" />}
          </div>
        </div>
      </div>
    );
  }

  // ── Grid card (the home page) ──
  return (
    <div className="group feed-post-contain">
      <Link to={watchPath}>
        <Thumbnail video={video} className="aspect-video" />
      </Link>

      <div className="flex items-start gap-2.5 mt-2">
        <Link to={videoChannelPath(video.pubkey)} className="shrink-0 mt-0.5">
          {profile?.picture ? (
            <img
              src={profile.picture}
              alt=""
              loading="lazy"
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/10" />
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <Link to={watchPath}>
            <h3 className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
              {video.title}
            </h3>
          </Link>

          <Link
            to={videoChannelPath(video.pubkey)}
            className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
          >
            <span className="truncate max-w-[10rem]">{name}</span>
            <UserBadges pubkey={video.pubkey} size="sm" showTooltip={false} />
          </Link>

          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5 text-[10px] text-muted-foreground">
            {/* Page views, not "views": BCHNostr cannot see plays on the
                provider, and this counter is per-browser. */}
            {localViews > 0 && (
              <span className="flex items-center gap-0.5" title="Times you opened this video's page in this browser">
                <Eye className="w-2.5 h-2.5" /> {formatCount(localViews)}
              </span>
            )}
            {published && <span>{published}</span>}
            {engagement && engagement.uniqueTippers > 0 && (
              <span
                className="flex items-center gap-0.5 text-primary font-medium"
                title={`${engagement.uniqueTippers} people tipped this video`}
              >
                <BCHLogo size={9} /> {engagement.uniqueTippers}
              </span>
            )}
            {engagement && engagement.comments > 0 && (
              <span className="flex items-center gap-0.5">
                <MessageCircle className="w-2.5 h-2.5" /> {formatCount(engagement.comments)}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {boost && <PromotionBadge boost={boost} />}
            <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-semibold">
              {videoCategoryLabel(video.category)}
            </span>
            <VideoLabel video={video} />
            <div
              className="no-navigate ml-auto"
              onClick={e => { e.preventDefault(); e.stopPropagation(); }}
            >
              <VideoTipControl
                video={video}
                recipientProfile={profile}
                withEventId
                dropdownAlign="right"
                compact
              />
            </div>
          </div>

          {/* Transparency: why this card is here, in the viewer's own terms. */}
          {reasons && reasons.length > 0 && (
            <p className="text-[10px] text-muted-foreground/70 mt-1 truncate">
              {reasons.slice(0, 2).join(' · ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────

export function VideoCardSkeleton({ variant = 'grid' }: { variant?: 'grid' | 'row' }) {
  if (variant === 'row') {
    return (
      <div className="flex gap-2.5">
        <div className="w-[9.5rem] sm:w-[10.5rem] aspect-video rounded-xl bg-muted animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5 py-1">
          <div className="h-3 rounded bg-muted animate-pulse" />
          <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="aspect-video rounded-xl bg-muted animate-pulse" />
      <div className="flex gap-2.5 mt-2">
        <div className="w-8 h-8 rounded-full bg-muted animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 rounded bg-muted animate-pulse" />
          <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
        </div>
      </div>
    </div>
  );
}
